import { Dispatch, PayloadAction, createSlice } from "@reduxjs/toolkit";
import { UserDto } from "../api/identity";
import {
    AccessDto,
    AccountDetailDto,
    AccountInvoiceInformationDto,
    AccountService,
    BusinessLine,
    FolderDto,
    FolderService,
    ProjectDto,
    ProjectRelatedEntitiesContainerDto,
    ProjectService,
    RegistrationService,
} from "../api/psc";

import { AccountService as IdentityAccountService } from "../api/identity";

import { setInitialMapAccess } from "../features/projects/slice/MapSlice";
import { setInitialProjectAccess } from "../features/projects/slice/ProjectSlice";
import { setActivitiesInitialState } from "./ActivitySlice";
import { showExtraDetails } from "./ProjectDetailSlice";
import { setSalesInitialState } from "./SalesSlice";
import { addOrUpdateFolderAction, deleteFolderAction } from "./SharedActions";

export interface SharedState {
    accountDetail: AccountDetailDto;
    userProfile: UserDto;
    accessList: AccessDto[];
    folders: FolderDto[];
    baseInfo: ProjectRelatedEntitiesContainerDto;
    businessLines: BusinessLine[];
    batchOperationProjects: ProjectDto[];
    projectDetailIndex: number;
    accountInvoiceInformation: AccountInvoiceInformationDto | null;
}

const initialState: SharedState = {
    accountDetail: null,
    userProfile: null,
    accessList: [],
    folders: [],
    baseInfo: null,
    businessLines: [],
    batchOperationProjects: [],
    projectDetailIndex: 0,
    accountInvoiceInformation: null,
};

const sharedSlice = createSlice({
    name: "shared",
    initialState,
    reducers: {
        setSharedStateRequirements(
            state: SharedState,
            action: PayloadAction<{
                accountDetail: AccountDetailDto;
                folders: FolderDto[];
                relatedEntities: ProjectRelatedEntitiesContainerDto;
                businessLines: BusinessLine[];
                user: UserDto;
            }>
        ) {
            return {
                ...state,
                folders: action.payload.folders,
                baseInfo: action.payload.relatedEntities,
                accountDetail: action.payload.accountDetail,
                accessList: action.payload.accountDetail.accessList,
                businessLines: action.payload.businessLines,
                userProfile: action.payload.user,
            };
        },
        updateAccountOnboardingStep(state: SharedState) {
            return {
                ...state,
                accountDetail: {
                    ...state.accountDetail,
                    shouldRunOnboarding: false,
                },
            };
        },
        clearSelectedProjects(state: SharedState, _action: PayloadAction) {
            return { ...state, batchOperationProjects: [] };
        },
        setProjectDetailIndex(
            state: SharedState,
            action: PayloadAction<number>
        ) {
            return { ...state, projectDetailIndex: action.payload };
        },
        setSelectedProjects(
            state: SharedState,
            action: PayloadAction<ProjectDto[]>
        ) {
            return { ...state, batchOperationProjects: action.payload };
        },
        toggleProjectSelection(
            state: SharedState,
            action: PayloadAction<ProjectDto>
        ) {
            const batchOperationProjects = [...state.batchOperationProjects];

            if (
                batchOperationProjects.some(
                    (p) => p.hashId === action.payload.hashId
                )
            ) {
                const project = batchOperationProjects.find(
                    (p) => p.hashId === action.payload.hashId
                );
                const index = batchOperationProjects.indexOf(project);
                batchOperationProjects.splice(index, 1);
            } else {
                batchOperationProjects.push(action.payload);
            }

            return { ...state, batchOperationProjects: batchOperationProjects };
        },
        onTermsOfServicesAccepted(state: SharedState, action: PayloadAction) {
            return {
                ...state,
                accountDetail: {
                    ...state.accountDetail,
                    agreedToTermsOfServices: true,
                },
            };
        },
        onUserNationCodeAuthenticated(
            state: SharedState,
            action: PayloadAction<boolean>
        ) {
            return {
                ...state,
                accountDetail: {
                    ...state.accountDetail,
                    shouldAuthenticate: action.payload ? false : true,
                },
            };
        },
        updateAccess(state: SharedState, action: PayloadAction<AccessDto>) {
            // for when the access is null
            // this happens if no matching access if found
            if (!action.payload) {
                return;
            }

            const accessToBeUpdate = state.accessList.find(
                (a) => a.id === action.payload.id
            );

            const updatedAccess: AccessDto = {
                ...accessToBeUpdate,
                viewCounter: action.payload.viewCounter,
            };

            const updatedAccessList = [...state.accessList];

            const indexOfUpdatedAccess =
                updatedAccessList.indexOf(accessToBeUpdate);

            if (indexOfUpdatedAccess !== -1) {
                updatedAccessList[indexOfUpdatedAccess] = updatedAccess;
            }

            for (const access of state.accessList) {
                if (access.viewCounter.id === action.payload.viewCounter.id) {
                    const indexOfAccessWithUpdateViewCounter =
                        updatedAccessList.indexOf(access);

                    if (indexOfAccessWithUpdateViewCounter !== -1) {
                        updatedAccessList[indexOfAccessWithUpdateViewCounter] =
                            {
                                ...access,
                                viewCounter: action.payload.viewCounter,
                            };
                    }
                }
            }

            return { ...state, accessList: updatedAccessList };
        },
    },
    extraReducers: (builder) => {
        builder.addCase(
            addOrUpdateFolderAction,
            (state: SharedState, action: PayloadAction<FolderDto>) => {
                let updatedFolders = [...state.folders];
                const note = updatedFolders.find(
                    (n) => n.id === action.payload.id
                );

                if (note) {
                    const index = updatedFolders.indexOf(note);
                    updatedFolders[index] = action.payload;
                } else {
                    updatedFolders.push(action.payload);
                }

                return { ...state, folders: updatedFolders };
            }
        );

        builder.addCase(
            deleteFolderAction,
            (state: SharedState, action: PayloadAction<number>) => {
                let updatedFolders = [
                    ...state.folders.filter((n) => n.id !== action.payload),
                ];

                return { ...state, folders: updatedFolders };
            }
        );
    },
});

export const fetchSharedRequirements = async (
    dispatch: Dispatch
) => {
    const v = await Promise.all([
        AccountService.getAccountDetail("1"),
        ProjectService.getProjectRelatedEntities("1"),
        FolderService.getUserFolders("1"),
        RegistrationService.listBusinessLines("1"),
        IdentityAccountService.getProfile(),
    ]);

    if (v[0].accessList.length > 0) {
        dispatch(setInitialProjectAccess(v[0]));
        dispatch(setInitialMapAccess(v[0].accessList[0]));
    } else {
        dispatch(setInitialProjectAccess(null));
        dispatch(setInitialMapAccess(null));
    }

    dispatch(
        setSharedStateRequirements({
            accountDetail: v[0],
            relatedEntities: v[1],
            folders: v[2],
            businessLines: v[3],
            user: v[4],
        })
    );

    dispatch(
        setSalesInitialState({
            currentUser: v[0].currentUser,
            users: v[0].users,
        })
    );

    dispatch(
        setActivitiesInitialState({
            currentUser: v[0].currentUser,
            users: v[0].users,
            reminderNotificationBadgeCount: 0,
            taskNotificationBadgeCount: 0,
        })
    );

    if ((v[0].options & 2) === 2) {
        dispatch(showExtraDetails());
    }
};

export const {
    setSharedStateRequirements,
    clearSelectedProjects,
    setProjectDetailIndex,
    setSelectedProjects,
    toggleProjectSelection,
    updateAccess,
    onTermsOfServicesAccepted,
    onUserNationCodeAuthenticated,
    updateAccountOnboardingStep,
} = sharedSlice.actions;
export default sharedSlice.reducer;
